import { ReactNode, useEffect } from "react";
import { useRouter } from "next/router";
import { EntryPoint } from "@memento/types/subscription";
import { useAuth } from "src/hooks/useAuth";
import constants from "src/constants";
import { getRedirectParams } from "src/libs/urlLib";
import { useSubscription } from "src/hooks/useSubscription";
import { LoadingStatus } from "src/libs/fetcher";
import { getSharedAlbumsLandingPageUrl } from "src/libs/shared-albums-lib";

type GuardUnauthenticatedProps = {
  children: ReactNode;
};

function getRedirectUrlFromEntryPoint(entryPoint: EntryPoint): string {
  switch (entryPoint) {
    case "shared-albums":
      return getSharedAlbumsLandingPageUrl();
    case "group-videos":
      return `/${constants.slugs.cardLink}`;
  }
}

// cannot view this page if authenticated (can only view if unauthenticated)
export function GuardUnauthenticated({ children }: GuardUnauthenticatedProps) {
  const router = useRouter();
  const { isLoggedIn, isAuthInProgress } = useAuth();
  const { hasActiveSubscription, status: subscriptionStatus } =
    useSubscription();

  useEffect(() => {
    if (isAuthInProgress) {
      return;
    }

    // wait for a subscription
    if (subscriptionStatus === LoadingStatus.LOADING) {
      return;
    }

    // we only want to redirect if the user is logged in
    if (!isLoggedIn) {
      return;
    }

    const { upgradeFromUrl, redirectUrl, entryPoint } = getRedirectParams(
      router.query
    );

    if (!redirectUrl) {
      router.push(getRedirectUrlFromEntryPoint(entryPoint));
    } else if (hasActiveSubscription) {
      const redirectIsUpgrade = redirectUrl?.includes("/upgrade");

      // we don't want to redirect people with active subs back to the upgrade page
      if (redirectIsUpgrade) {
        router.push(upgradeFromUrl || redirectUrl);
      } else {
        router.push(redirectUrl);
      }
    } else {
      router.push(
        {
          pathname: redirectUrl,
          query: {
            ...(upgradeFromUrl && { from: upgradeFromUrl }),
          },
        },
        // this hides the query parameters in the URL
        redirectUrl || undefined
      );
    }
  }, [isLoggedIn, isAuthInProgress, subscriptionStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isLoggedIn && !isAuthInProgress) {
    return <>{children}</>;
  } else {
    return null;
  }
}
